import { useEffect, useRef } from 'react';

import { usePageVisibility } from '#src/hooks/usePageVisibility.tsx';

/**
 * Runs `refresh` each time the page goes from hidden to visible — never on
 * mount, and never on a `visibilitychange` that did not cross that boundary.
 *
 * Polling cannot cover this: an interval is throttled in a hidden tab and does
 * not fire at all on a frozen page (backgrounded mobile webview, sleeping
 * laptop), so a tab can come back holding badly outdated data.
 */
export const useRefreshOnPageVisible = (refresh: () => Promise<unknown>) => {
  const isVisible = usePageVisibility();
  const wasVisible = useRef(isVisible);

  useEffect(() => {
    const cameBack = isVisible && !wasVisible.current;
    wasVisible.current = isVisible;
    if (!cameBack) return;
    refresh().catch(() => {
      // Best effort. Callers must not depend on this having succeeded.
    });
  }, [isVisible, refresh]);
};
